import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Clock, AlertCircle, ClipboardCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import SafetyInvestigationForm from './SafetyInvestigationForm';

interface SafetyReport {
  id: string;
  user_id: string;
  issue_type: string;
  description: string;
  severity: string;
  conversation_id: string | null;
  contact_permission: boolean;
  status: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  resolution_notes: string | null;
  created_at: string;
  profiles: {
    display_name: string;
    email?: string;
  } | null;
}

export default function SafetyReportsManager() {
  const [reports, setReports] = useState<SafetyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<SafetyReport | null>(null);
  const [showInvestigation, setShowInvestigation] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'reviewed'>('pending');

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('adverse_events')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter === 'pending') {
        query = query.eq('status', 'pending');
      } else if (filter === 'reviewed') {
        query = query.eq('status', 'reviewed');
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch user profiles separately
      const reportsWithProfiles = await Promise.all(
        (data || []).map(async (report: any) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('user_id', report.user_id)
            .single();
          
          return {
            ...report,
            profiles: profile || { display_name: 'Unknown User' }
          };
        })
      );
      
      setReports(reportsWithProfiles as any);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load safety reports');
    } finally {
      setLoading(false);
    }
  };

  const handleStartInvestigation = () => {
    setShowInvestigation(true);
  };

  const handleInvestigationComplete = async (investigation: any) => {
    if (!selectedReport) return;
    
    setResolving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('adverse_events')
        .update({
          status: 'reviewed',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id,
          resolution_notes: JSON.stringify(investigation),
        })
        .eq('id', selectedReport.id);

      if (error) throw error;

      toast.success('Investigation completed and report closed');
      setSelectedReport(null);
      setShowInvestigation(false);
      loadReports();
    } catch (error) {
      console.error('Error completing investigation:', error);
      toast.error('Failed to save investigation');
    } finally {
      setResolving(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'high':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'medium':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-blue-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'high':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'medium':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default:
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Safety Reports</h2>
              <p className="text-sm text-muted-foreground">
                User-reported safety concerns requiring review
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('pending')}
              >
                <Clock className="h-4 w-4 mr-2" />
                Pending
              </Button>
              <Button
                variant={filter === 'reviewed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('reviewed')}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Reviewed
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
            </div>
          </div>

          {reports.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No {filter === 'pending' ? 'pending' : filter === 'reviewed' ? 'reviewed' : ''} reports</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => (
                <Card
                  key={report.id}
                  className={`p-4 cursor-pointer hover:bg-accent/50 transition-colors ${
                    report.status === 'pending' ? 'border-l-4 border-l-orange-500' : ''
                  }`}
                  onClick={() => {
                    setSelectedReport(report);
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {getSeverityIcon(report.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={getSeverityColor(report.severity)}>
                            {report.severity.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">{report.issue_type.replace(/_/g, ' ')}</Badge>
                          {report.status === 'reviewed' && (
                            <Badge variant="outline" className="bg-green-500/10 text-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Reviewed
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium mb-1">
                          {report.profiles?.display_name || 'Unknown User'}
                        </p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {report.description}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {format(new Date(report.created_at), 'PPp')}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Dialog open={!!selectedReport && !showInvestigation} onOpenChange={(open) => {
        if (!open) {
          setSelectedReport(null);
          setShowInvestigation(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReport && getSeverityIcon(selectedReport.severity)}
              Safety Report Overview
            </DialogTitle>
            <DialogDescription>
              Review report details before starting formal investigation
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline" className={getSeverityColor(selectedReport.severity)}>
                  {selectedReport.severity.toUpperCase()}
                </Badge>
                <Badge variant="outline">{selectedReport.issue_type.replace(/_/g, ' ')}</Badge>
                {selectedReport.status === 'reviewed' && (
                  <Badge variant="outline" className="bg-green-500/10 text-green-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Investigated
                  </Badge>
                )}
              </div>

              <Card className="p-4 bg-accent/30 border-primary/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Investigation Required</p>
                    <p className="text-sm text-muted-foreground">
                      All safety reports must be formally investigated using the structured checklist to ensure compliance.
                    </p>
                  </div>
                </div>
              </Card>

              <div className="space-y-2">
                <h4 className="font-semibold">Reporter</h4>
                <p className="text-sm">{selectedReport.profiles?.display_name || 'Unknown User'}</p>
                {selectedReport.contact_permission && (
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-500">
                    Contact Permission Granted
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">Description</h4>
                <p className="text-sm whitespace-pre-wrap bg-accent/20 p-3 rounded-md">{selectedReport.description}</p>
              </div>

              {selectedReport.conversation_id && (
                <div className="space-y-2">
                  <h4 className="font-semibold">Associated Conversation ID</h4>
                  <code className="text-xs bg-accent/20 p-2 rounded block">{selectedReport.conversation_id}</code>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="font-semibold">Report Submitted</h4>
                <p className="text-sm">{format(new Date(selectedReport.created_at), 'PPpp')}</p>
              </div>

              {selectedReport.status === 'reviewed' && selectedReport.resolution_notes && (
                <div className="space-y-2">
                  <h4 className="font-semibold">Investigation Results</h4>
                  <div className="bg-accent/20 p-3 rounded-md">
                    {(() => {
                      try {
                        const investigation = JSON.parse(selectedReport.resolution_notes);
                        return (
                          <div className="space-y-2 text-sm">
                            <p><strong>Investigator:</strong> {investigation.investigator_name}</p>
                            <p><strong>Risk Level:</strong> {investigation.risk_assessment.toUpperCase()}</p>
                            <p><strong>Root Cause:</strong> {investigation.root_cause}</p>
                            <p className="pt-2 border-t"><strong>Summary:</strong> {investigation.resolution_summary}</p>
                            {investigation.follow_up_required && (
                              <Badge variant="outline" className="mt-2">Follow-up Required: {investigation.follow_up_date}</Badge>
                            )}
                          </div>
                        );
                      } catch {
                        return <p className="text-sm whitespace-pre-wrap">{selectedReport.resolution_notes}</p>;
                      }
                    })()}
                  </div>
                  {selectedReport.reviewed_at && (
                    <p className="text-xs text-muted-foreground">
                      Completed {format(new Date(selectedReport.reviewed_at), 'PPpp')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setSelectedReport(null)}>
              Close
            </Button>
            {selectedReport?.status === 'pending' && (
              <Button onClick={handleStartInvestigation}>
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Start Formal Investigation
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Investigation Dialog */}
      <Dialog open={showInvestigation} onOpenChange={(open) => {
        if (!open) {
          setShowInvestigation(false);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Safety Investigation Proforma
            </DialogTitle>
            <DialogDescription>
              Complete all required steps to close this report compliantly
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <SafetyInvestigationForm
              reportId={selectedReport.id}
              issueType={selectedReport.issue_type}
              severity={selectedReport.severity}
              description={selectedReport.description}
              conversationId={selectedReport.conversation_id}
              onComplete={handleInvestigationComplete}
              onCancel={() => setShowInvestigation(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
